/**
 * `trace-mcp prune` command.
 *
 * Scans `~/.trace-mcp/index/` and classifies every `.db` file as one of:
 *   - live                  → project root still registered AND directory exists
 *   - orphan_missing_root   → project root no longer on disk
 *   - orphan_unregistered   → DB exists but root isn't in registry (non-session)
 *   - session_active        → session DB younger than --session-ttl-days
 *   - session_expired       → session DB older than the TTL
 *   - stray_small           → tiny (<5 indexed files) + cold (>30d) project DB
 *
 * `trace-mcp prune` is dry-run by default. Pass `--apply` to delete; pass
 * `--aggressive` to additionally drop `stray_small` candidates.
 *
 * Also exposed programmatically as {@link pruneIndexDir} so the daemon can
 * run a quiet `session_expired`-only sweep at startup.
 */

import fs from 'node:fs';
import path from 'node:path';
import * as p from '@clack/prompts';
import Database from 'better-sqlite3';
import { Command } from 'commander';
import {
  DECISIONS_DB_PATH,
  ensureGlobalDirs,
  EPHEMERAL_INDEX_DIR,
  projectHash,
  projectName,
  STATUS_DIR,
  TOPOLOGY_DB_PATH,
  TRACE_MCP_HOME,
} from '../global.js';
import { logger } from '../logger.js';
import {
  findOrphanHolderDirs,
  hasLiveHolderOrUnknown,
  sweepOrphanHolderDirs,
} from '../db-holders.js';
import {
  findEphemeralProjects,
  listProjects,
  pruneStaleProjects,
  unregisterProject,
} from '../registry.js';
import { INDEX_DIR } from '../shared/paths.js';
import { deleteStatusSentinelsForRoot } from '../server/heartbeat.js';
import { TopologyStore } from '../topology/topology-db.js';
import { DecisionStore } from '../memory/decision-store.js';
import { sweepSessionFiles } from '../session/sweeper.js';
import { findPrunableProjectConfigSections, pruneProjectConfigSections } from '../config-jsonc.js';
import {
  findOrphanTmpFilesUnderHome,
  sweepOrphanTmpFilesUnderHome,
} from '../utils/atomic-write.js';
import {
  DB_FAMILY_SUFFIXES,
  deleteDbFamily,
  findOrphanDbSidecars,
  sweepOrphanDbSidecars,
} from '../utils/db-family.js';

/** Categories assigned to each DB candidate. */
export type PruneCategory =
  | 'live'
  | 'orphan_missing_root'
  | 'orphan_unregistered'
  | 'session_active'
  | 'session_expired'
  | 'stray_small';

export interface DbCandidate {
  /** Absolute path of the base `.db` file (no -wal/-shm suffix). */
  path: string;
  /** Base filename, e.g. `myproj-abc123def456.db`. */
  basename: string;
  /** Total bytes for the DB + its SQLite sidecars (-wal/-shm/-journal). */
  bytes: number;
  /** mtime of the base file in epoch ms. */
  mtimeMs: number;
  /** Category assigned to this DB. */
  category: PruneCategory;
  /** Hash chunk extracted from the filename, when one was found. */
  hash: string | null;
  /** Project root the registry claims this hash maps to (if any). */
  registeredRoot: string | null;
}

export interface PruneOptions {
  /** Maximum age (in days) before a session DB is considered expired. */
  sessionTtlDays?: number;
  /** When false (default), don't unlink anything — only classify. */
  apply?: boolean;
  /** When true (+ apply), also drop stray_small candidates. */
  aggressive?: boolean;
  /** Categories to act on when apply=true.
   *  When set, restricts deletion to this set. */
  onlyCategories?: PruneCategory[];
}

export interface PruneSummary {
  candidates: DbCandidate[];
  /** Per-category totals — counts AND bytes. */
  totals: Record<PruneCategory, { count: number; bytes: number }>;
  /** Files actually deleted (empty when apply=false). */
  deleted: string[];
  /** Total bytes freed by this run. */
  freedBytes: number;
}

const DEFAULT_SESSION_TTL_DAYS = 7;
/** Default grace for a top-level DB with no registry row (TRA-1908). */
export const DEFAULT_TOP_LEVEL_ORPHAN_TTL_DAYS = 7;
const STRAY_SMALL_MIN_FILES = 5;
const STRAY_SMALL_MIN_AGE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const SQLITE_SIDECARS = ['', '-wal', '-shm', '-journal'] as const;

function emptyTotals(): Record<PruneCategory, { count: number; bytes: number }> {
  return {
    live: { count: 0, bytes: 0 },
    orphan_missing_root: { count: 0, bytes: 0 },
    orphan_unregistered: { count: 0, bytes: 0 },
    session_active: { count: 0, bytes: 0 },
    session_expired: { count: 0, bytes: 0 },
    stray_small: { count: 0, bytes: 0 },
  };
}

function totalBytesWithSidecars(basePath: string): { bytes: number; mtimeMs: number } {
  let bytes = 0;
  let mtimeMs = 0;
  for (const suffix of SQLITE_SIDECARS) {
    try {
      const stat = fs.statSync(basePath + suffix);
      bytes += stat.size;
      if (suffix === '') mtimeMs = stat.mtimeMs;
    } catch {
      /* missing sidecar — fine */
    }
  }
  return { bytes, mtimeMs };
}

/** Pull the trailing 12-char hex hash out of `<name>-<hash>.db` (or session form). */
function extractHash(basename: string): string | null {
  // Session form: `<name>-<hash>-session-<sessionId>.db`
  const sessionMatch = basename.match(/^(.+)-([0-9a-f]{12})-session-[0-9a-f-]+\.db$/i);
  if (sessionMatch) return sessionMatch[2].toLowerCase();
  // Task-cache form: `daemon-task-cache-<sessionId>-<hash>.db`
  const taskMatch = basename.match(/^daemon-task-cache-[0-9a-f-]+-([0-9a-f]{12})\.db$/i);
  if (taskMatch) return taskMatch[1].toLowerCase();
  // Plain project form: `<name>-<hash>.db`
  const plainMatch = basename.match(/^(.+)-([0-9a-f]{12})\.db$/i);
  if (plainMatch) return plainMatch[2].toLowerCase();
  return null;
}

function isSessionFile(basename: string): boolean {
  return /-session-[0-9a-f-]+\.db$/i.test(basename) || basename.startsWith('daemon-task-cache-');
}

/** Count files+symbols in a project DB. Returns nulls if the DB can't be opened. */
function inspectProjectDb(dbPath: string): { files: number | null } {
  try {
    const db = new Database(dbPath, { readonly: true, fileMustExist: true });
    try {
      // Schema may not have a `files` table on older DBs — fall back to 0.
      try {
        const row = db.prepare('SELECT COUNT(*) AS n FROM files').get() as
          | { n: number }
          | undefined;
        return { files: row?.n ?? 0 };
      } catch {
        return { files: 0 };
      }
    } finally {
      db.close();
    }
  } catch {
    return { files: null };
  }
}

interface RegistryHashIndex {
  /**
   * Map from projectHash → every live anchor (root + exists) that resolves
   * to it. A hash can have more than one anchor once TRA-38 dbPath sharing
   * is in play — two checkouts of the same git remote deliberately share
   * one index DB, so that DB's hash is "live" as long as ANY of its
   * anchors' roots still exist, not just the one this map happened to see
   * first.
   */
  byHash: Map<string, { root: string; exists: boolean }[]>;
}

function addHashAnchor(
  byHash: Map<string, { root: string; exists: boolean }[]>,
  hash: string,
  root: string,
): void {
  const anchor = { root, exists: fs.existsSync(root) };
  const anchors = byHash.get(hash);
  if (anchors) anchors.push(anchor);
  else byHash.set(hash, [anchor]);
}

function buildRegistryIndex(): RegistryHashIndex {
  const byHash = new Map<string, { root: string; exists: boolean }[]>();
  const entries = listProjects();
  // Standalone registry roots. A multi-root child that is ALSO registered on
  // its own keeps its own DB live; a declared child with no standalone row
  // has no canonical child DB (multi-root convert deletes those on the spot),
  // so a child-hash DB file left on disk is a stale leftover, not live
  // (TRA-2055: assetfeed/*, thewed-novakit, top100-laravel-upgrade).
  const standaloneRoots = new Set(entries.map((e) => path.resolve(e.root)));
  for (const entry of entries) {
    const absRoot = path.resolve(entry.root);
    // Entry-level `dbPath` hash — names in registry can drift from a fresh
    // path-based recompute (most notably: TRA-38 dbPath sharing, where a
    // second checkout's *own* path hashes to something no file on disk ever
    // used), but the DB the registry actually points at is canonical.
    const dbHash = extractHash(path.basename(entry.dbPath));
    if (dbHash) addHashAnchor(byHash, dbHash, absRoot);
    // Path-hash anchor only when it IS the canonical DB. For a TRA-38 shared
    // entry the registry points at a sibling's DB (dbHash !== pathHash), so a
    // path-hash-named file on disk is the stale pre-sharing predecessor
    // (TRA-2055: trace-mcp-ad05ed8c4be8 vs canonical 6b30abaa68c1), not live.
    const pathHash = projectHash(absRoot);
    if (!dbHash || pathHash === dbHash) {
      addHashAnchor(byHash, pathHash, absRoot);
    }
    // Multi-root: REGISTERED children also count as live anchors.
    if (entry.children) {
      for (const child of entry.children) {
        const absChild = path.resolve(child);
        if (!standaloneRoots.has(absChild)) continue;
        addHashAnchor(byHash, projectHash(absChild), absChild);
      }
    }
  }
  return { byHash };
}

/** Classify every DB file under INDEX_DIR. */
export function scanIndexDir(options: PruneOptions = {}): DbCandidate[] {
  const ttlDays = options.sessionTtlDays ?? DEFAULT_SESSION_TTL_DAYS;
  const now = Date.now();
  const ttlCutoff = now - ttlDays * DAY_MS;
  const strayCutoff = now - STRAY_SMALL_MIN_AGE_DAYS * DAY_MS;

  let files: string[];
  try {
    files = fs.readdirSync(INDEX_DIR);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code !== 'ENOENT') {
      logger.warn({ err }, 'prune: failed to list INDEX_DIR');
    }
    return [];
  }

  const registry = buildRegistryIndex();
  const candidates: DbCandidate[] = [];

  for (const file of files) {
    // TRA-1714: the `ephemeral/` subdirectory holds per-run one-shot workdir
    // DBs that are never in registry.json by design (TRA-396) — a "file vs
    // registry" reconciliation would class every one of them, live ones
    // included, as `orphan_unregistered`. `sweepEphemeralDbs` owns that
    // directory by age instead, so this scan must never descend into it.
    // (Non-recursive today, so the `.db` filter below already skips the
    // directory entry — this guard pins the intent against a future refactor.)
    if (file === 'ephemeral') continue;
    // Skip sidecars — we walk each base DB once and aggregate companions.
    if (/(-wal|-shm|-journal)$/.test(file)) continue;
    if (!file.endsWith('.db')) continue;

    const full = path.join(INDEX_DIR, file);
    const { bytes, mtimeMs } = totalBytesWithSidecars(full);
    const hash = extractHash(file);
    const session = isSessionFile(file);

    let category: PruneCategory;
    let registeredRoot: string | null = null;
    const anchors = hash ? registry.byHash.get(hash) : undefined;
    // TRA-38: a hash can have multiple anchors (dbPath shared across
    // checkouts of the same git remote) — prefer an existing one for
    // display, but any one of them is enough to keep this DB "live".
    const liveAnchor = anchors?.find((a) => a.exists);
    if (anchors && anchors.length > 0) registeredRoot = (liveAnchor ?? anchors[0]).root;

    // TRA-304: a live holder marker means a process has this DB open right
    // now. That outranks every "looks orphaned" heuristic below — the root it
    // belongs to may never have made it into the registry, and an unreadable
    // holder dir counts as held, because guessing wrong here deletes data.
    const held = !session && hasLiveHolderOrUnknown(full);

    if (session) {
      category = mtimeMs < ttlCutoff ? 'session_expired' : 'session_active';
    } else if (held) {
      category = 'live';
    } else if (!anchors || anchors.length === 0) {
      category = 'orphan_unregistered';
    } else if (!liveAnchor) {
      category = 'orphan_missing_root';
    } else {
      // Live project — check stray_small heuristic for very small + old DBs.
      // We only flag stray when the user actually opts into aggressive mode;
      // classification still happens so dry-run output shows them.
      const ageOk = mtimeMs > 0 && mtimeMs < strayCutoff;
      let strayCandidate = false;
      if (ageOk) {
        const insp = inspectProjectDb(full);
        if (insp.files !== null && insp.files < STRAY_SMALL_MIN_FILES) {
          strayCandidate = true;
        }
      }
      category = strayCandidate ? 'stray_small' : 'live';
    }

    candidates.push({
      path: full,
      basename: file,
      bytes,
      mtimeMs,
      category,
      hash,
      registeredRoot,
    });
  }

  return candidates;
}

/** Delete a base DB + its whole family via the shared helper (TRA-1864). */
function unlinkDb(basePath: string): { deleted: string[]; bytes: number } {
  const { deleted, freedBytes } = deleteDbFamily(basePath);
  return { deleted, bytes: freedBytes };
}

/** Run the full prune algorithm. Idempotent. */
export function pruneIndexDir(options: PruneOptions = {}): PruneSummary {
  ensureGlobalDirs();

  const candidates = scanIndexDir(options);
  const totals = emptyTotals();
  for (const c of candidates) {
    totals[c.category].count += 1;
    totals[c.category].bytes += c.bytes;
  }

  const summary: PruneSummary = {
    candidates,
    totals,
    deleted: [],
    freedBytes: 0,
  };

  if (!options.apply) return summary;

  const defaultDeletable: PruneCategory[] = [
    'orphan_missing_root',
    'orphan_unregistered',
    'session_expired',
  ];
  const allowed = new Set<PruneCategory>(options.onlyCategories ?? defaultDeletable);
  if (options.aggressive && !options.onlyCategories) {
    allowed.add('stray_small');
  }

  for (const c of candidates) {
    if (!allowed.has(c.category)) continue;
    const { deleted, bytes } = unlinkDb(c.path);
    summary.deleted.push(...deleted);
    summary.freedBytes += bytes;
  }

  return summary;
}

export interface TopLevelOrphanSweepResult {
  /** Base `.db` paths actually deleted (whole family via {@link deleteDbFamily}). */
  removed: string[];
  /** `orphan_unregistered` candidates seen by this run. */
  scannedOrphans: number;
  /** Of those, how many were kept because family mtime is still inside the TTL. */
  retainedWithinTtl: number;
}

/**
 * TTL sweep for top-level index DBs with no registry row (TRA-1908).
 *
 * The night QA contour (TRA-1709) found 718 files (~1.1 GB) under
 * `~/.trace-mcp/index/*.db*` referenced by no `dbPath` in registry.json —
 * 266 `.db` older than 7 days, mostly `trace-mcp-*` / `app-*` / `workdir-*`
 * leftovers of old naming schemes and razed throwaway checkouts. The
 * presence-based sweeps never reach them (nothing is "missing" — there is
 * no row at all), and `softGcSweep` deliberately never touches
 * `orphan_unregistered` without an age gate (a DB with no row could just be
 * a project not yet re-added), so they sat forever.
 *
 * This is the age-gated counterpart, mirroring `sweepEphemeralDbs` (TRA-396):
 * an unregistered top-level `.db` whose whole family (WAL/SHM/journal plus
 * the watcher snapshot — a fresh snapshot means a live watcher still walks
 * that root, TRA-1714) stayed untouched for `maxAgeDays` is dead by the same
 * argument the ephemeral sweep applies. Registry matching is reused from
 * {@link scanIndexDir} (canonical `dbPath` hash + path hash for non-shared
 * entries + registered multi-root children, so TRA-38 shared-dbPath siblings
 * count as live while stale pre-sharing predecessors do not — TRA-2055),
 * session files keep their own
 * TTL (`session_active` never lands here), and a live holder marker vetoes
 * deletion — re-checked right before unlink for TOCTOU safety on top of the
 * `live` classification the scan already applies.
 */
export function sweepTopLevelOrphanDbs(
  maxAgeDays = DEFAULT_TOP_LEVEL_ORPHAN_TTL_DAYS,
): TopLevelOrphanSweepResult {
  const candidates = scanIndexDir();
  const orphans = candidates.filter((c) => c.category === 'orphan_unregistered');
  const cutoff = Date.now() - maxAgeDays * DAY_MS;
  const removed: string[] = [];
  let retainedWithinTtl = 0;

  for (const c of orphans) {
    // Newest mtime across the whole family is the clock: an active run
    // keeps writing the WAL/snapshot even when the base `.db` looks old.
    let newestMtime = 0;
    for (const suffix of DB_FAMILY_SUFFIXES) {
      try {
        newestMtime = Math.max(newestMtime, fs.statSync(c.path + suffix).mtimeMs);
      } catch {
        /* sidecar absent */
      }
    }
    if (newestMtime === 0) continue; // raced away between scan and sweep — keep it
    if (newestMtime >= cutoff) {
      retainedWithinTtl += 1;
      continue;
    }
    if (hasLiveHolderOrUnknown(c.path)) continue;
    unlinkDb(c.path);
    removed.push(c.path);
  }

  return { removed, scannedOrphans: orphans.length, retainedWithinTtl };
}

export interface OrphanStatusSweepResult {
  /** Absolute sentinel paths actually deleted (files unlinked, consulted dirs removed). */
  removed: string[];
  /** Sentinel entries whose hash maps to no registry root and no index DB. */
  scannedOrphans: number;
  /** Of those, how many were kept because mtime is still inside the TTL. */
  retainedWithinTtl: number;
  /** Entries kept because the hash still belongs to a live project. */
  retainedLive: number;
}

/** Default grace for a status sentinel with no project behind it (TRA-2062). */
export const DEFAULT_ORPHAN_STATUS_TTL_DAYS = 1;

/**
 * TTL sweep for status sentinels whose project is gone (TRA-2062).
 *
 * The night QA contour (TRA-1709) found 866 files under `~/.trace/status/`
 * with 187 stale-only project hashes — heartbeat files of long-deleted
 * ephemeral agent workdirs. `sweepStaleSentinels` (heartbeat.ts) only collects
 * by 7-day mtime, and no deregistration path removed them at all, so every
 * agent workdir left 3–4 files behind forever.
 *
 * This is the gated backstop: a sentinel (`trace-mcp-status-<hash>.json`,
 * `trace-mcp-alive-<hash>`, `trace-mcp-consulted-<hash>`) is dead only when
 * its hash maps to NO registry root (path hash, canonical dbPath hash, or
 * registered multi-root child) AND no index DB basename (top-level or
 * `ephemeral/`) still carries it — and even then only past `maxAgeDays`, so a
 * project mid-registration is never collected. A false positive is
 * self-healing anyway: the next heartbeat flush (≤30s) recreates the file.
 *
 * Everything else in STATUS_DIR (daemon snapshot, bypass files, user files)
 * is never matched and never touched.
 */
export function sweepOrphanStatusSentinels(
  maxAgeDays = DEFAULT_ORPHAN_STATUS_TTL_DAYS,
): OrphanStatusSweepResult {
  const result: OrphanStatusSweepResult = {
    removed: [],
    scannedOrphans: 0,
    retainedWithinTtl: 0,
    retainedLive: 0,
  };
  let names: string[];
  try {
    names = fs.readdirSync(STATUS_DIR);
  } catch {
    return result; // never created — no server has run here
  }

  // Live hashes: every registered root's path hash + canonical dbPath hash +
  // registered multi-root children (same anchor set scanIndexDir uses).
  const liveHashes = new Set<string>();
  try {
    for (const entry of listProjects()) {
      liveHashes.add(projectHash(path.resolve(entry.root)));
      const dbHash = extractHash(path.basename(entry.dbPath));
      if (dbHash) liveHashes.add(dbHash);
      for (const child of entry.children ?? []) {
        liveHashes.add(projectHash(path.resolve(child)));
      }
    }
  } catch {
    // Fail toward keeping: an unreadable registry must not cost live sentinels.
    return result;
  }

  // Index-DB presence: a hash still carried by any DB basename (top-level or
  // ephemeral/) belongs to a project, registered or mid-flight.
  const indexHashes = new Set<string>();
  for (const dir of [INDEX_DIR, EPHEMERAL_INDEX_DIR]) {
    let files: string[];
    try {
      files = fs.readdirSync(dir);
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.endsWith('.db')) continue;
      const hash = extractHash(file);
      if (hash) indexHashes.add(hash);
    }
  }

  const cutoff = Date.now() - maxAgeDays * DAY_MS;
  for (const name of names) {
    const match = name.match(/^trace-mcp-(status|alive|consulted)-([0-9a-f]{12})(\.json)?$/i);
    if (!match) continue;
    // `status` always carries `.json`, `alive`/`consulted` never do — a name
    // mixing those is not ours.
    const kind = match[1].toLowerCase();
    const hasJson = match[3] !== undefined;
    if (kind === 'status' ? !hasJson : hasJson) continue;
    const hash = match[2].toLowerCase();
    if (liveHashes.has(hash) || indexHashes.has(hash)) {
      result.retainedLive += 1;
      continue;
    }
    result.scannedOrphans += 1;
    const full = path.join(STATUS_DIR, name);
    let mtimeMs = 0;
    let isDir = false;
    let isLink = false;
    try {
      const st = fs.lstatSync(full);
      isLink = st.isSymbolicLink();
      isDir = st.isDirectory();
      mtimeMs = st.mtimeMs;
    } catch {
      continue; // raced away between readdir and sweep — keep it
    }
    if (isLink || mtimeMs >= cutoff) {
      result.retainedWithinTtl += 1;
      continue;
    }
    try {
      if (isDir) fs.rmSync(full, { recursive: true, force: true });
      else fs.unlinkSync(full);
      result.removed.push(full);
    } catch {
      /* vanished under us — fine */
    }
  }
  return result;
}

/** Self-check: walking projectHash + projectName here matches what indexer uses. */
function shortPath(p: string): string {
  const home = process.env.HOME ?? process.env.USERPROFILE ?? '';
  if (home && p.startsWith(home)) return `~${p.slice(home.length)}`;
  return p;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const CATEGORY_ORDER: PruneCategory[] = [
  'live',
  'session_active',
  'session_expired',
  'orphan_missing_root',
  'orphan_unregistered',
  'stray_small',
];

const CATEGORY_DESCRIPTIONS: Record<PruneCategory, string> = {
  live: 'live (project still registered and directory exists)',
  session_active: 'session_active (younger than TTL)',
  session_expired: 'session_expired (older than TTL — would delete)',
  orphan_missing_root: 'orphan_missing_root (project root deleted — would delete)',
  orphan_unregistered: 'orphan_unregistered (DB exists, not in registry — would delete)',
  stray_small: 'stray_small (tiny + cold — would delete with --aggressive)',
};

function printSummary(
  summary: PruneSummary,
  opts: { apply: boolean; aggressive: boolean; sessionTtlDays: number },
): void {
  const lines: string[] = [];
  lines.push(`Index dir: ${shortPath(INDEX_DIR)}`);
  lines.push(`Total candidates: ${summary.candidates.length}`);
  lines.push(`Session TTL: ${opts.sessionTtlDays} day(s)`);
  lines.push('');
  for (const cat of CATEGORY_ORDER) {
    const t = summary.totals[cat];
    if (t.count === 0) continue;
    lines.push(
      `  ${cat.padEnd(22)} ${String(t.count).padStart(4)}  ${fmtBytes(t.bytes)}  ${CATEGORY_DESCRIPTIONS[cat]}`,
    );
  }
  if (opts.apply) {
    lines.push('');
    lines.push(`Deleted ${summary.deleted.length} file(s), freed ${fmtBytes(summary.freedBytes)}`);
  }
  p.note(lines.join('\n'), opts.apply ? 'prune (applied)' : 'prune (dry-run)');
}

/** Format the summary as JSON for `--json`. */
function toJson(
  summary: PruneSummary,
  opts: { apply: boolean; aggressive: boolean; sessionTtlDays: number },
): unknown {
  const totals: Record<string, { count: number; bytes: number }> = {};
  for (const cat of CATEGORY_ORDER) {
    totals[cat] = summary.totals[cat];
  }
  return {
    indexDir: INDEX_DIR,
    apply: opts.apply,
    aggressive: opts.aggressive,
    sessionTtlDays: opts.sessionTtlDays,
    candidates: summary.candidates.map((c) => ({
      path: c.path,
      basename: c.basename,
      bytes: c.bytes,
      mtimeMs: c.mtimeMs,
      category: c.category,
      hash: c.hash,
      registeredRoot: c.registeredRoot,
    })),
    totals,
    deleted: summary.deleted,
    freedBytes: summary.freedBytes,
  };
}

export interface TopologyPruneSummary {
  staleServices: Array<{ id: number; name: string; repo_root: string }>;
  staleSubprojects: Array<{ id: number; name: string; repo_root: string; project_root: string }>;
  removedServices: number;
  removedSubprojects: number;
}

export interface DecisionsPruneSummary {
  staleRoots: string[];
  staleDecisionsCount: number;
  removedDecisions: number;
  removedChunks: number;
  removedClusters: number;
  removedMemos: number;
}

export function scanOrPruneTopology(apply = false): TopologyPruneSummary {
  if (!fs.existsSync(TOPOLOGY_DB_PATH)) {
    return {
      staleServices: [],
      staleSubprojects: [],
      removedServices: 0,
      removedSubprojects: 0,
    };
  }
  try {
    const topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
    try {
      const stale = topoStore.findStale();
      let removedServices = 0;
      let removedSubprojects = 0;
      if (apply && (stale.staleServices.length > 0 || stale.staleSubprojects.length > 0)) {
        const res = topoStore.pruneStale();
        removedServices = res.services;
        removedSubprojects = res.subprojects;
      }
      return {
        staleServices: stale.staleServices,
        staleSubprojects: stale.staleSubprojects,
        removedServices,
        removedSubprojects,
      };
    } finally {
      topoStore.close();
    }
  } catch (err) {
    logger.warn({ err }, 'prune: topology scan/prune failed');
    return {
      staleServices: [],
      staleSubprojects: [],
      removedServices: 0,
      removedSubprojects: 0,
    };
  }
}

export function scanOrPruneDecisions(apply = false): DecisionsPruneSummary {
  if (!fs.existsSync(DECISIONS_DB_PATH)) {
    return {
      staleRoots: [],
      staleDecisionsCount: 0,
      removedDecisions: 0,
      removedChunks: 0,
      removedClusters: 0,
      removedMemos: 0,
    };
  }
  try {
    const store = new DecisionStore(DECISIONS_DB_PATH);
    try {
      const stale = store.findStale();
      let removedDecisions = 0;
      let removedChunks = 0;
      let removedClusters = 0;
      let removedMemos = 0;
      if (apply && stale.staleRoots.length > 0) {
        const res = store.pruneStale({ staleRoots: stale.staleRoots, includeMinedSessions: true });
        removedDecisions = res.decisions;
        removedChunks = res.chunks;
        removedClusters = res.clusters;
        removedMemos = res.memos;
      }
      return {
        staleRoots: stale.staleRoots,
        staleDecisionsCount: stale.decisionsCount,
        removedDecisions,
        removedChunks,
        removedClusters,
        removedMemos,
      };
    } finally {
      store.close();
    }
  } catch (err) {
    logger.warn({ err }, 'prune: decisions scan/prune failed');
    return {
      staleRoots: [],
      staleDecisionsCount: 0,
      removedDecisions: 0,
      removedChunks: 0,
      removedClusters: 0,
      removedMemos: 0,
    };
  }
}

export function scanOrPruneConfig(apply: boolean): {
  prunableSections: string[];
  removedSections: string[];
} {
  const prunableSections = findPrunableProjectConfigSections();
  const removedSections = apply && prunableSections.length > 0 ? pruneProjectConfigSections() : [];
  return { prunableSections, removedSections };
}

export function scanOrPruneTmpFiles(apply: boolean): {
  staleTmpFiles: string[];
  removedTmpFiles: string[];
} {
  const staleTmpFiles = findOrphanTmpFilesUnderHome(TRACE_MCP_HOME);
  const removedTmpFiles =
    apply && staleTmpFiles.length > 0 ? sweepOrphanTmpFilesUnderHome(TRACE_MCP_HOME) : [];
  return { staleTmpFiles, removedTmpFiles };
}

export function scanOrPruneHolderDirs(apply: boolean): {
  orphanHolderDirs: string[];
  removedHolderDirs: string[];
} {
  const orphanHolderDirs = findOrphanHolderDirs(INDEX_DIR).map((g) => g.dir);
  const removedHolderDirs =
    apply && orphanHolderDirs.length > 0 ? sweepOrphanHolderDirs(INDEX_DIR) : [];
  return { orphanHolderDirs, removedHolderDirs };
}

export const pruneCommand = new Command('prune')
  .description(
    'Audit ~/.trace-mcp/index for orphan/expired DBs (dry-run by default; use --apply to delete)',
  )
  .option('--apply', 'Actually delete orphan + expired session DBs')
  .option('--aggressive', 'Also delete stray small (<5 files) DBs older than 30 days')
  .option('--session-ttl-days <n>', 'Session DB TTL in days (default 7)', '7')
  .option('--json', 'Emit JSON instead of a human report')
  .action(
    async (opts: {
      apply?: boolean;
      aggressive?: boolean;
      sessionTtlDays?: string;
      json?: boolean;
    }) => {
      const sessionTtlDays = Math.max(0, parseInt(opts.sessionTtlDays ?? '7', 10) || 7);
      const apply = !!opts.apply;
      const aggressive = !!opts.aggressive;

      if (!opts.json) p.intro('trace-mcp prune');

      const summary = pruneIndexDir({ apply, aggressive, sessionTtlDays });

      // Stale registry rows (folder deleted) cause "Project not found" at runtime
      // (#168). The DB-file sweep above doesn't touch registry.json, so prune
      // them here too: report on dry-run, remove on --apply.
      const staleRegistryRoots = listProjects()
        .filter((e) => !fs.existsSync(e.root))
        .map((e) => e.root);
      const removedRegistryRoots = apply ? pruneStaleProjects() : [];
      // TRA-2062: pruneStaleProjects unregisters without removeProjectArtifacts,
      // so drop each removed root's status sentinels here (best-effort).
      if (apply) {
        for (const root of removedRegistryRoots) {
          try {
            deleteStatusSentinelsForRoot(root);
          } catch {
            /* best-effort */
          }
        }
      }

      // Ephemeral projects left over from past agent runs (>24h old)
      const staleEphemeralRoots = findEphemeralProjects().map((e) => e.root);
      const removedEphemeralRoots: string[] = [];
      if (apply) {
        for (const root of staleEphemeralRoots) {
          unregisterProject(root);
          removedEphemeralRoots.push(root);
          // TRA-2062: same litter as above.
          try {
            deleteStatusSentinelsForRoot(root);
          } catch {
            /* best-effort */
          }
        }
      }

      // Stale topology rows (services/subprojects pointing to deleted folders)
      const topoSummary = scanOrPruneTopology(apply);

      // Stale decisions / memory rows belonging to deleted project roots
      const decisionsSummary = scanOrPruneDecisions(apply);

      // Stale session artifacts (snapshots >24h, end logs >7d, orphan summaries)
      const sessionSummary = sweepSessionFiles({ dryRun: !apply });

      // Dead / overflowing sections in .config.json
      const configSummary = scanOrPruneConfig(apply);

      // Orphan atomic-write tmp files in state directories
      const tmpFilesSummary = scanOrPruneTmpFiles(apply);

      // TRA-2055: holders-only orphans — `.db.holders/` dirs whose stem `.db`
      // is gone (deleted externally, or never created at the claimed stem).
      // `findOrphanDbSidecars` never sees them (no WAL/SHM/snapshot beside
      // them). Dry-run lists; --apply deletes.
      const holdersSummary = scanOrPruneHolderDirs(apply);

      // TRA-1864: stem-less WAL/SHM/journal sidecars (+ snapshots) whose
      // `.db` is gone. `scanIndexDir` above only walks base `.db` files, so
      // without this these orphans are invisible to every other category.
      // Dry-run lists them; --apply deletes.
      const orphanFound = apply ? [] : findOrphanDbSidecars();
      const orphanSwept = apply ? sweepOrphanDbSidecars() : { deleted: [], freedBytes: 0 };
      const orphanSidecarBytes = apply
        ? orphanSwept.freedBytes
        : orphanFound.reduce((n, g) => n + g.bytes, 0);
      const orphanSidecarCount = apply
        ? orphanSwept.deleted.length
        : orphanFound.reduce((n, g) => n + g.files.length, 0);

      // TRA-2062: status sentinels whose project is gone (no registry root, no
      // index DB) past the TTL. --apply deletes and reports; dry-run skips —
      // the stale-registry/ephemeral sections above already name the roots
      // whose sentinels an apply would drop, and a delete-on-dry-run sweep
      // would lie about "would remove".
      const orphanStatusSwept = apply
        ? sweepOrphanStatusSentinels()
        : { removed: [], scannedOrphans: 0, retainedWithinTtl: 0, retainedLive: 0 };

      if (opts.json) {
        console.log(
          JSON.stringify(
            {
              ...(toJson(summary, { apply, aggressive, sessionTtlDays }) as object),
              staleRegistryRoots,
              removedRegistryRoots,
              staleEphemeralRoots,
              removedEphemeralRoots,
              topology: {
                staleServices: topoSummary.staleServices,
                staleSubprojects: topoSummary.staleSubprojects,
                removedServices: topoSummary.removedServices,
                removedSubprojects: topoSummary.removedSubprojects,
              },
              decisions: {
                staleRoots: decisionsSummary.staleRoots,
                staleDecisionsCount: decisionsSummary.staleDecisionsCount,
                removedDecisions: decisionsSummary.removedDecisions,
                removedChunks: decisionsSummary.removedChunks,
                removedClusters: decisionsSummary.removedClusters,
                removedMemos: decisionsSummary.removedMemos,
              },
              sessions: {
                deletedFiles: sessionSummary.deleted.length,
                freedBytes: sessionSummary.freedBytes,
              },
              config: {
                prunableSections: configSummary.prunableSections,
                removedSections: configSummary.removedSections,
              },
              tmpFiles: {
                staleTmpFiles: tmpFilesSummary.staleTmpFiles,
                removedTmpFiles: tmpFilesSummary.removedTmpFiles,
              },
              orphanSidecars: {
                count: orphanSidecarCount,
                freedBytes: orphanSidecarBytes,
                deleted: orphanSwept.deleted,
              },
              orphanHolderDirs: {
                count: apply
                  ? holdersSummary.removedHolderDirs.length
                  : holdersSummary.orphanHolderDirs.length,
                removed: holdersSummary.removedHolderDirs,
                dirs: apply ? [] : holdersSummary.orphanHolderDirs,
              },
              orphanStatusSentinels: {
                removed: orphanStatusSwept.removed,
                scannedOrphans: orphanStatusSwept.scannedOrphans,
                retainedWithinTtl: orphanStatusSwept.retainedWithinTtl,
                retainedLive: orphanStatusSwept.retainedLive,
              },
            },
            null,
            2,
          ),
        );
        return;
      }

      printSummary(summary, { apply, aggressive, sessionTtlDays });
      if (staleRegistryRoots.length > 0) {
        const heading = apply
          ? `Removed ${removedRegistryRoots.length} stale registry entr${removedRegistryRoots.length === 1 ? 'y' : 'ies'}:`
          : `${staleRegistryRoots.length} stale registry entr${staleRegistryRoots.length === 1 ? 'y' : 'ies'} (folder deleted) — would remove:`;
        p.note(
          [heading, ...staleRegistryRoots.map((r) => `  ${shortPath(r)}`)].join('\n'),
          'Registry',
        );
      }
      if (staleEphemeralRoots.length > 0) {
        const heading = apply
          ? `Removed ${removedEphemeralRoots.length} stale ephemeral workdir registration(s):`
          : `${staleEphemeralRoots.length} stale ephemeral workdir registration(s) — would remove:`;
        p.note(
          [heading, ...staleEphemeralRoots.map((r) => `  ${shortPath(r)}`)].join('\n'),
          'Ephemeral Projects',
        );
      }
      if (
        topoSummary.staleServices.length > 0 ||
        topoSummary.staleSubprojects.length > 0 ||
        topoSummary.removedServices > 0 ||
        topoSummary.removedSubprojects > 0
      ) {
        const heading = apply
          ? `Removed ${topoSummary.removedServices} stale service(s) and ${topoSummary.removedSubprojects} stale subproject(s) from topology.db:`
          : `${topoSummary.staleServices.length} stale service(s) and ${topoSummary.staleSubprojects.length} stale subproject(s) in topology.db (folder deleted) — would remove:`;
        const lines = [heading];
        for (const s of topoSummary.staleServices) {
          lines.push(`  [service] ${s.name} (${shortPath(s.repo_root)})`);
        }
        for (const sub of topoSummary.staleSubprojects) {
          lines.push(`  [subproject] ${sub.name} (${shortPath(sub.repo_root)})`);
        }
        p.note(lines.join('\n'), 'Topology');
      }
      if (decisionsSummary.staleRoots.length > 0 || decisionsSummary.removedDecisions > 0) {
        const heading = apply
          ? `Removed ${decisionsSummary.removedDecisions} orphaned decision(s) across ${decisionsSummary.staleRoots.length} deleted project root(s) from decisions.db:`
          : `${decisionsSummary.staleDecisionsCount} orphaned decision(s) across ${decisionsSummary.staleRoots.length} deleted project root(s) in decisions.db (folder deleted) — would remove:`;
        const lines = [heading];
        for (const r of decisionsSummary.staleRoots) {
          lines.push(`  ${shortPath(r)}`);
        }
        p.note(lines.join('\n'), 'Decision Memory');
      }
      if (sessionSummary.deleted.length > 0) {
        const freedKb = Math.round(sessionSummary.freedBytes / 1024);
        const heading = apply
          ? `Removed ${sessionSummary.deleted.length} stale session file(s) (${freedKb} KB freed):`
          : `${sessionSummary.deleted.length} stale session file(s) (${freedKb} KB) — would remove:`;
        const lines = [heading];
        for (const f of sessionSummary.deleted.slice(0, 10)) {
          lines.push(`  ${f}`);
        }
        if (sessionSummary.deleted.length > 10) {
          lines.push(`  ... and ${sessionSummary.deleted.length - 10} more`);
        }
        p.note(lines.join('\n'), 'Sessions');
      }
      if (configSummary.prunableSections.length > 0 || configSummary.removedSections.length > 0) {
        const heading = apply
          ? `Removed ${configSummary.removedSections.length} dead project section(s) from .config.json:`
          : `${configSummary.prunableSections.length} dead project section(s) in .config.json — would remove:`;
        const list = apply ? configSummary.removedSections : configSummary.prunableSections;
        p.note([heading, ...list.map((r) => `  ${shortPath(r)}`)].join('\n'), 'Global Config');
      }
      if (tmpFilesSummary.staleTmpFiles.length > 0 || tmpFilesSummary.removedTmpFiles.length > 0) {
        const heading = apply
          ? `Removed ${tmpFilesSummary.removedTmpFiles.length} orphaned atomic write .tmp file(s):`
          : `${tmpFilesSummary.staleTmpFiles.length} orphaned atomic write .tmp file(s) — would remove:`;
        const list = apply ? tmpFilesSummary.removedTmpFiles : tmpFilesSummary.staleTmpFiles;
        p.note([heading, ...list.map((f) => `  ${shortPath(f)}`)].join('\n'), 'Orphan Tmp Files');
      }
      if (orphanSidecarCount > 0) {
        const heading = apply
          ? `Removed ${orphanSwept.deleted.length} orphaned index-DB sidecar(s) (WAL/SHM without a DB, ${fmtBytes(orphanSwept.freedBytes)} freed):`
          : `${orphanSidecarCount} orphaned index-DB sidecar(s) (WAL/SHM without a DB, ${fmtBytes(orphanSidecarBytes)}) — would remove:`;
        const list = apply
          ? orphanSwept.deleted.slice(0, 10)
          : orphanFound.flatMap((g) => g.files).slice(0, 10);
        const lines = [heading, ...list.map((f) => `  ${shortPath(f)}`)];
        if (orphanSidecarCount > 10) {
          lines.push(`  ... and ${orphanSidecarCount - 10} more`);
        }
        p.note(lines.join('\n'), 'Orphan Sidecars');
      }
      if (
        holdersSummary.orphanHolderDirs.length > 0 ||
        holdersSummary.removedHolderDirs.length > 0
      ) {
        const heading = apply
          ? `Removed ${holdersSummary.removedHolderDirs.length} orphaned index-DB holder dir(s) (stem .db gone):`
          : `${holdersSummary.orphanHolderDirs.length} orphaned index-DB holder dir(s) (stem .db gone) — would remove:`;
        const list = apply ? holdersSummary.removedHolderDirs : holdersSummary.orphanHolderDirs;
        const lines = [heading, ...list.slice(0, 10).map((f) => `  ${shortPath(f)}`)];
        if (list.length > 10) {
          lines.push(`  ... and ${list.length - 10} more`);
        }
        p.note(lines.join('\n'), 'Orphan Holder Dirs');
      }
      if (apply && orphanStatusSwept.removed.length > 0) {
        const lines = [
          `Removed ${orphanStatusSwept.removed.length} orphaned status sentinel(s) (project gone, past TTL):`,
          ...orphanStatusSwept.removed.slice(0, 10).map((f) => `  ${shortPath(f)}`),
        ];
        if (orphanStatusSwept.removed.length > 10) {
          lines.push(`  ... and ${orphanStatusSwept.removed.length - 10} more`);
        }
        p.note(lines.join('\n'), 'Orphan Status Sentinels');
      }
      if (!apply) {
        p.outro('Dry-run only — re-run with --apply to delete.');
      } else {
        p.outro('Prune complete.');
      }
    },
  );

// Re-export the indexer name helpers so daemon code can build canonical paths
// without circling through here. Keeps `INDEX_DIR + name + hash` agreement.
export { projectHash, projectName };
